import React, { useState, useCallback, useEffect } from "react";
import ImageLightbox from "./ImageLightbox";
import type { ImageUploadItem } from "../../../../types/chat-agent";
import { MAX_IMAGES_COUNT } from "@utils/fileUpload";
import "./ImageGallery.scss";
import { ErrorImageIcon } from "@assets/icon";
import { safePostMessage } from "@utils/index";

interface ImageGalleryProps {
  imageUrls: string[];
  imageUploadItems?: ImageUploadItem[];
}

const getDisplayUrl = (
  index: number,
  imageUrls: string[],
  imageUploadItems?: ImageUploadItem[]
): string => {
  if (imageUploadItems && imageUploadItems[index]) {
    return imageUploadItems[index].s3Url || imageUploadItems[index].localUrl;
  }
  return imageUrls[index] || '';
};

const getImageStatus = (
  index: number,
  imageUploadItems?: ImageUploadItem[]
): 'uploading' | 'success' | 'failed' | null => {
  if (!imageUploadItems || !imageUploadItems[index]) return null;
  return imageUploadItems[index].status;
};

const ImageOverlay: React.FC<{
  status: 'uploading' | 'success' | 'failed' | null;
}> = ({ status }) => {
  if (!status || status === 'success') return null;

  if (status === 'uploading') {
    return (
      <div className="image-gallery__overlay-bar">
        <div className="image-gallery__dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="image-gallery__overlay-bar">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#E54646" />
        </svg>
        <span style={{ color: '#E54646', fontSize: '13px', fontWeight: 500 }}>Chưa thể gửi</span>
      </div>
    );
  }

  return null;
};

const GalleryItem: React.FC<{
  url: string;
  index: number;
  status: 'uploading' | 'success' | 'failed' | null;
  className?: string;
  onClick: () => void;
  moreCount?: number;
}> = ({ url, status, className = '', onClick, moreCount }) =>{
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    setUseFallback(false);
  }, [url]);

  const handleImgError = useCallback(() => {
    setUseFallback((prev) => (prev ? prev : true));
  }, []);


  return (
    <button
      className={`image-gallery__item ${className}`}
      onClick={(status === 'uploading' || useFallback) ? undefined : onClick}
      disabled={(status === 'uploading' || useFallback)}
      type="button"
    >
      {
        useFallback ? (
          <>
          <div className="image-gallery__item-img--fallback">
              <ErrorImageIcon/>
              <div className="image-gallery__item-img--fallback-text">Ảnh không tồn tại</div>
          </div>
          </>
        ) : (
          <>
            <img src={url} alt="image" loading="lazy" onError={handleImgError}/>
            <ImageOverlay status={status} />
            {moreCount && moreCount > 0 && (
              <div className="image-gallery__more-overlay">
                +{moreCount}
              </div>
            )}
          </>
        )
      }
    </button>
  )
} ;

const ImageGallery: React.FC<ImageGalleryProps> = ({
  imageUrls,
  imageUploadItems,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = useCallback((index: number) => {
    const status = getImageStatus(index, imageUploadItems);
    if (status === 'uploading' || status === 'failed') return;
    setLightboxIndex(index);
  }, [imageUploadItems]);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  const count = imageUploadItems?.length || imageUrls.length;

  if (count === 0) return null;

  const lightboxUrls = imageUploadItems
    ? imageUploadItems
      .filter(item => item.status === 'success' || !item.status)
      .map(item => item.s3Url || item.localUrl)
    : imageUrls;

  const renderItem = (index: number, className: string = '', moreCount?: number) => (
    <GalleryItem
      key={index}
      url={getDisplayUrl(index, imageUrls, imageUploadItems)}
      index={index}
      status={getImageStatus(index, imageUploadItems)}
      className={className}
      // onClick={() => openLightbox(index)}
      onClick={() => {
        safePostMessage({
          type: 'CHATBOT_IMAGE_GALLERY',
          payload: {
            images: lightboxUrls,
            currentIndex: index,
          },
        });
      }}
      moreCount={moreCount}
    />
  );

  const renderLightbox = () =>
    lightboxIndex !== null && lightboxUrls.length > 0 ? (
      <ImageLightbox
        images={lightboxUrls}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onChangeIndex={setLightboxIndex}
      />
    ) : null;

  const visibleCount = Math.min(count, MAX_IMAGES_COUNT);
  const lastIdx = visibleCount - 1;

  return (
    <>
      <div className="image-gallery image-gallery--strip">
        {Array.from({ length: visibleCount }, (_, i) =>
          renderItem(
            i,
            'image-gallery__item--square image-gallery__item--strip-cell',
            i === lastIdx && count > MAX_IMAGES_COUNT ? count - MAX_IMAGES_COUNT : undefined,
          ),
        )}
      </div>
      {renderLightbox()}
    </>
  );
};

export default ImageGallery;